import type { Ref } from 'vue'
import type { GtmCommonData } from '@/interfaces/IGtm'
import type { VerificationInfo } from '@/interfaces/verificationInfo'
import { useUserInfoQuery } from '@nami/apis'
import { SboCurrency } from '@nami/domain/classes'
import { Currency } from '@nami/domain/enums'
import { useAsyncState } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { getGtmCommonData } from '@/apis/gtmApi'
import { userInfoApi } from '@/apis/userInfoApi'
import { UserInfo } from '@/classes/UserInfo'
import { AccountTier } from '@/enum/AccountTier'

interface IUserStoreState {
    userInfo: Ref<UserInfo>
    isUserInfoReady: Ref<boolean>
    gtmCommonData: Ref<GtmCommonData>
    isGtmDataReady: Ref<boolean>
    editableVerificationInfo: Ref<VerificationInfo>
    toggleDisplayChannel: Ref<boolean>
}

const getUserInfo = async () => {
    const { data: userInfo, suspense } = useUserInfoQuery()
    await suspense()

    return userInfo.value!
}

export const useUserStore = defineStore('UserStore', {
    state: (): IUserStoreState => {
        const { state: userInfo, isReady: isUserInfoReady } = useAsyncState(
            () => getUserInfo(),
            new UserInfo({
                firstName: '',
                lastName: '',
                fullName: '',
                isInitialDepositDone: false,
                initDepositExpiredDay: 0,
                isKyc: false,
                accountTier: AccountTier.New,
                createdOn: new Date(),
                accountId: '',
                customerId: 0,
                katakana: null,
                cpf: null,
                currency: new SboCurrency({
                    code: Currency.UNKNOWN,
                    displayName: '',
                }),
                isRequiredKyc: false,
                residentCountryCode: '',
            }),
        )
        const { state: gtmCommonData, isReady: isGtmDataReady } = useAsyncState(() => getGtmCommonData(), {
            currency: '',
            identify: '',
            jurisdiction: '',
            trackingId: '',
            residentCountryCode: '',
        })

        const editableVerificationInfo = ref<VerificationInfo>({
            katakana: null,
        })

        const { state: toggleDisplayChannel } = useAsyncState(() => userInfoApi.toggleDisplayChannel(), true)

        watch(
            isUserInfoReady,
            (ready) => {
                if (ready && userInfo.value) {
                    editableVerificationInfo.value.katakana = userInfo.value.katakana
                }
            },
            { once: true },
        )

        return {
            userInfo,
            isUserInfoReady,
            gtmCommonData,
            isGtmDataReady,
            editableVerificationInfo,
            toggleDisplayChannel,
        }
    },
    actions: {
        isUserCurrency(userCurrency: Currency) {
            return this.userInfo.currency.code === userCurrency
        },
    },
    getters: {
        isNeedToFillInKatakana(): boolean {
            if (!this.isUserInfoReady) {
                return false
            }
            return this.userInfo.currency.code === Currency.JPY && !this.userInfo.katakana
        },
        isValidKatakana(): boolean {
            if (!this.editableVerificationInfo.katakana) {
                return false
            }

            return Boolean(/^[\u30A0-\u30FF]+$/.test(this.editableVerificationInfo.katakana.trim()))
        },
    },
})
